import React, { useEffect, useMemo, useState } from 'react';
import useAuthStore from '../../../core/stores/authStore';
import { usePointDistributions } from '../../hooks/point/usePointDistributions';
import { usePointKpi, usePointStats } from '../../hooks/point/usePointStatus';
import {
  fmt,
  ymd,
  toStatRows,
  mdhm,
  Trend,
  KpiGrid,
  ResetButton,
  SearchBar,
  RadioGroup,
  DateRange,
  OptionRow,
  Pager,
  rangeText,
} from './PointShared';
import styles from './PointV2.module.css';

/* 팜포인트 현황 — Figma 386:12963(일별) / 431:14081(주별) / 431:14610(월별) */

const UNIT_OPTIONS = [
  { key: 'day', label: '일별' },
  { key: 'week', label: '주별' },
  { key: 'month', label: '월별' },
];
const PRESET_OPTIONS = [
  { key: 'yesterday', label: '어제' },
  { key: 'lastWeek', label: '지난주' },
  { key: 'lastMonth', label: '지난달' },
  { key: 'thisMonth', label: '이번달' },
];
const TYPE_OPTIONS = [
  { key: 'all', label: '전체' },
  { key: 'EARN', label: '적립' },
  { key: 'USE', label: '사용' },
];
const TYPE_LABEL = { EARN: '적립', USE: '사용', CANCEL: '취소', REFUND: '환불', EXPIRE: '만료' };
const HISTORY_PAGE_SIZE = 8;

const startOfDay = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const mondayOf = d => addDays(startOfDay(d), -((d.getDay() + 6) % 7));

/* 시안 annotation: 기본 조회기간 — 일별 오늘 포함 14일 / 주별 현재 주 포함 12주 / 월별 현재 월 포함 12개월 */
const defaultRange = unit => {
  const today = startOfDay(new Date());
  if (unit === 'week') return { start: addDays(mondayOf(today), -7 * 11), end: today };
  if (unit === 'month') return { start: new Date(today.getFullYear(), today.getMonth() - 11, 1), end: today };
  return { start: addDays(today, -13), end: today };
};

const presetRange = key => {
  const today = startOfDay(new Date());
  if (key === 'yesterday') {
    const y = addDays(today, -1);
    return { start: y, end: y };
  }
  if (key === 'lastWeek') {
    const mon = addDays(mondayOf(today), -7);
    return { start: mon, end: addDays(mon, 6) };
  }
  if (key === 'lastMonth') {
    return { start: new Date(today.getFullYear(), today.getMonth() - 1, 1), end: new Date(today.getFullYear(), today.getMonth(), 0) };
  }
  return { start: new Date(today.getFullYear(), today.getMonth(), 1), end: today };
};

const PointStatus = () => {
  const brn = useAuthStore(s => s.pharmacy?.business_registration_number);

  // ── 기간별 현황 ──
  const [unit, setUnit] = useState('day');
  const [statsRange, setStatsRange] = useState(() => defaultRange('day'));
  const statsQuery = usePointStats(brn, { unit, start_date: ymd(statsRange.start), end_date: ymd(statsRange.end) });
  const stats = useMemo(() => toStatRows(statsQuery.data ?? [], unit, startOfDay(new Date())), [statsQuery.data, unit]);
  const kpiQuery = usePointKpi(brn, ymd(new Date()).slice(0, 7));
  const k = kpiQuery.data;
  const changeUnit = u => {
    setUnit(u);
    setStatsRange(defaultRange(u));
  };

  // ── 적립·사용 이력 (기존 /point/pharmacy-transactions) ──
  const [keyword, setKeyword] = useState('');
  // 고객명 검색은 서버가 전체 거래를 훑어 거르므로 입력 300ms 멈춘 뒤 요청
  const [searchKeyword, setSearchKeyword] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchKeyword(keyword.trim()), 300);
    return () => clearTimeout(t);
  }, [keyword]);
  const [preset, setPreset] = useState('thisMonth');
  const [histRange, setHistRange] = useState(() => presetRange('thisMonth'));
  const [type, setType] = useState('all');
  const [page, setPage] = useState(1);

  const params = useMemo(
    () => ({
      start_date: ymd(histRange.start),
      end_date: ymd(histRange.end),
      ...(type !== 'all' && { transaction_type: type }),
      // 고객명 검색 (v2 H-3, 약국 토큰일 때 서버가 user_name 으로 필터)
      ...(searchKeyword && { keyword: searchKeyword }),
      limit: HISTORY_PAGE_SIZE,
      offset: (page - 1) * HISTORY_PAGE_SIZE,
    }),
    [histRange, type, searchKeyword, page]
  );
  const historyQuery = usePointDistributions(brn, params);
  const items = historyQuery.data?.items ?? [];
  const total = historyQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE));

  const withReset = fn => v => {
    fn(v);
    setPage(1);
  };
  const resetHistory = () => {
    setKeyword('');
    setPreset('thisMonth');
    setHistRange(presetRange('thisMonth'));
    setType('all');
    setPage(1);
  };

  // 값이 없으면(로딩·에러·null) '-' — 가짜 기본값 없음
  const kpiVal = (v, unitText) => (v == null ? '-' : `${fmt(v)}${unitText}`);
  const kpis = [
    {
      label: '이번 달 적립',
      value: kpiVal(k?.earned, 'P'),
      right: k?.earned_delta_pct != null ? <Trend value={k.earned_delta_pct} suffix="%" /> : null,
    },
    {
      label: '이번달 사용',
      value: kpiVal(k?.used, 'P'),
      right: k?.use_rate_pct != null ? <span className={styles.kpiNote}>사용률 {k.use_rate_pct}%</span> : null,
    },
    { label: '누적 발행 잔액', value: kpiVal(k?.outstanding_balance, 'P') },
    { label: '적립 회원', value: kpiVal(k?.member_count, '명') },
  ];

  const tall = unit !== 'day';

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <KpiGrid items={kpis} />

        {/* 기간별 현황 386:13194 */}
        <section className={styles.section}>
          <div className={styles.sectionTitleRow}>
            <h2 className={styles.sectionTitle}>기간별 현황</h2>
            <ResetButton onClick={() => changeUnit('day')} />
          </div>
          <div className={styles.block}>
            <div className={styles.options}>
              <OptionRow label="조회기간">
                <DateRange range={statsRange} onChange={setStatsRange} />
              </OptionRow>
              <OptionRow label="집계 단위" narrow type>
                <RadioGroup options={UNIT_OPTIONS} value={unit} onChange={changeUnit} />
              </OptionRow>
            </div>
            <div className={styles.table}>
              <div className={styles.thead}>
                <div className={styles.cell} style={{ minWidth: 120 }}>일자</div>
                <div className={`${styles.cell} ${styles.right}`} style={{ minWidth: 120 }}>적립</div>
                <div className={`${styles.cell} ${styles.right}`} style={{ minWidth: 120 }}>사용</div>
                <div className={`${styles.cell} ${styles.right}`} style={{ minWidth: 120 }}>순증감</div>
                <div className={`${styles.cell} ${styles.right}`} style={{ flex: '0 0 90px' }}>적립 건수</div>
                <div className={`${styles.cell} ${styles.right}`} style={{ flex: '0 0 90px' }}>사용 건수</div>
                <div className={`${styles.cell} ${styles.right} ${styles.cellNoBorder}`} style={{ flex: '0 0 90px' }}>활동 회원</div>
              </div>
              {stats.map(row => (
                <div key={row.start} className={`${styles.tr} ${tall ? styles.trTall : ''}`}>
                  <div className={styles.cell} style={{ minWidth: 120 }}>
                    {tall ? (
                      <div className={styles.periodText}>
                        <span className={styles.periodMain}>{row.label}</span>
                        <span className={styles.periodSub}>{row.sub}</span>
                      </div>
                    ) : (
                      <span>{row.label}</span>
                    )}
                    {row.in_progress && <span className={styles.progressTag}>진행중</span>}
                  </div>
                  <div className={`${styles.cell} ${styles.right} ${styles.medium} ${styles.blue}`} style={{ minWidth: 120 }}>
                    {row.earned ? '+' : ''}{fmt(row.earned)}P
                  </div>
                  <div className={`${styles.cell} ${styles.right} ${styles.medium} ${styles.red}`} style={{ minWidth: 120 }}>
                    {row.used ? '-' : ''}{fmt(row.used)}P
                  </div>
                  <div className={`${styles.cell} ${styles.right}`} style={{ minWidth: 120 }}>
                    {row.net_delta == null ? '-' : <Trend value={row.net_delta} />}
                  </div>
                  <div className={`${styles.cell} ${styles.right}`} style={{ flex: '0 0 90px' }}>{fmt(row.earn_count)}</div>
                  <div className={`${styles.cell} ${styles.right}`} style={{ flex: '0 0 90px' }}>{fmt(row.use_count)}</div>
                  <div className={`${styles.cell} ${styles.right} ${styles.cellNoBorder}`} style={{ flex: '0 0 90px' }}>
                    {fmt(row.active_members)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 적립 · 사용 이력 386:13195 */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>적립 · 사용 이력</h2>
          <div className={styles.block}>
            <div className={styles.block}>
              <div className={styles.searchContainer}>
                <SearchBar value={keyword} onChange={withReset(setKeyword)} placeholder="고객 이름 검색" />
                <ResetButton onClick={resetHistory} />
              </div>
              <div className={styles.options}>
                <OptionRow label="조회기간">
                  <DateRange
                    range={histRange}
                    onChange={r => {
                      setHistRange(r);
                      setPreset(null);
                      setPage(1);
                    }}
                  >
                    <RadioGroup
                      options={PRESET_OPTIONS}
                      value={preset}
                      onChange={k => {
                        setPreset(k);
                        setHistRange(presetRange(k));
                        setPage(1);
                      }}
                    />
                  </DateRange>
                </OptionRow>
                <OptionRow label="유형" narrow type>
                  <RadioGroup options={TYPE_OPTIONS} value={type} onChange={withReset(setType)} />
                </OptionRow>
              </div>
            </div>
            <div className={styles.table}>
              <div className={styles.thead}>
                <div className={styles.cell} style={{ minWidth: 140 }}>고객</div>
                <div className={`${styles.cell} ${styles.center}`} style={{ minWidth: 90, maxWidth: 100 }}>구분</div>
                <div className={styles.cell} style={{ minWidth: 140 }}>내용</div>
                <div className={`${styles.cell} ${styles.right}`} style={{ minWidth: 140 }}>포인트</div>
                <div className={`${styles.cell} ${styles.right}`} style={{ minWidth: 140 }}>잔여</div>
                <div className={styles.cell} style={{ minWidth: 120, maxWidth: 160 }}>일시</div>
              </div>
              {!historyQuery.isLoading && items.length === 0 && (
                <div className={styles.emptyRow}>적립·사용 내역이 없습니다</div>
              )}
              {historyQuery.isLoading || items.length === 0 ? null : (
                items.map((item, idx) => {
                  const minus = item.transaction_type === 'USE' || item.transaction_type === 'CANCEL' || item.transaction_type === 'EXPIRE';
                  return (
                    <div key={item.id ?? idx} className={styles.tr}>
                      <div className={styles.cell} style={{ minWidth: 140 }}>{item.user_name || item.user_id || '-'}</div>
                      <div className={`${styles.cell} ${styles.center}`} style={{ minWidth: 90, maxWidth: 100 }}>
                        <span className={styles.typeTag}>{TYPE_LABEL[item.transaction_type] || item.transaction_type}</span>
                      </div>
                      <div className={styles.cell} style={{ minWidth: 140 }}>{item.description || '-'}</div>
                      <div className={`${styles.cell} ${styles.right} ${styles.medium} ${minus ? styles.red : styles.blue}`} style={{ minWidth: 140 }}>
                        {minus ? '-' : '+'}
                        {fmt(item.amount)}P
                      </div>
                      <div className={`${styles.cell} ${styles.right} ${styles.medium} ${styles.dark}`} style={{ minWidth: 140 }}>
                        {item.balance_after != null ? `${fmt(item.balance_after)}P` : '-'}
                      </div>
                      <div className={styles.cell} style={{ minWidth: 120, maxWidth: 160 }}>{mdhm(item.created_at || item.timestamp)}</div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <Pager
            info={total ? `기간 내 ${fmt(total)}건 · ${rangeText(page, HISTORY_PAGE_SIZE, total)} 표시` : '기간 내 0건'}
            currentPage={page}
            totalPages={totalPages}
            onChange={setPage}
          />
        </section>
      </div>
    </div>
  );
};

export default PointStatus;
